/**
 * Assurance attribute reader — counts controls, evidence, risks, and
 * findings on a single record's attribute bag.
 *
 * The Process domain's `AssuranceReadModel` operates on `ProcessRecord`
 * rows pulled from the repository service. This helper does the same
 * shape of read but against a raw `Record<string, unknown>` attribute
 * bag, so canvas-side overlays (GrcBadges, AssuranceSelectionInspector)
 * and other domains (network resources backed by the Compass graph)
 * can share the key conventions without depending on the repository
 * read model.
 *
 * Keep this list in sync with `AssuranceReadModel.ts` so a node and
 * its inventory row report the same counts.
 */

const CONTROL_KEYS = ['controls', 'controlRefs', 'controlCandidates', 'controlPoints'] as const
const RISK_KEYS = ['risks', 'riskRefs', 'riskCandidates'] as const
const EVIDENCE_KEYS = ['evidence', 'evidenceRefs', 'evidenceRequirements'] as const
const FINDING_KEYS = ['findings', 'findingRefs'] as const

function countArrayLike(value: unknown): number {
  if (Array.isArray(value)) return value.length
  if (typeof value === 'string' && value.trim().length > 0) {
    return value.split(/[,;|]/).filter((piece) => piece.trim().length > 0).length
  }
  return 0
}

function sumKeys(attrs: Record<string, unknown>, keys: readonly string[]): number {
  let total = 0
  for (const key of keys) {
    total += countArrayLike(attrs[key])
  }
  return total
}

export interface AssuranceAttrSummary {
  controls: number
  evidence: number
  risks: number
  findings: number
  citations: number
  hasAnyData: boolean
}

export function summarizeAssuranceFromAttrs(
  attrs: Record<string, unknown> | undefined | null,
): AssuranceAttrSummary {
  const data = attrs ?? {}
  const controls = sumKeys(data, CONTROL_KEYS)
  const risks = sumKeys(data, RISK_KEYS)
  const findings = sumKeys(data, FINDING_KEYS)
  const evidenceArrays = sumKeys(data, EVIDENCE_KEYS)
  const evidenceStatus =
    typeof data['evidenceStatus'] === 'string' && data['evidenceStatus'].trim().length > 0 ? 1 : 0
  const evidence = evidenceArrays + evidenceStatus
  const citations = countArrayLike(data['citations']) + countArrayLike(data['sourceCitations'])
  const hasAnyData = controls + evidence + risks + findings + citations > 0
  return { controls, evidence, risks, findings, citations, hasAnyData }
}
